/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { dashboardsAddWidget } from "../funcs/dashboardsAddWidget.js";
import { dashboardsCreate } from "../funcs/dashboardsCreate.js";
import { dashboardsDelete } from "../funcs/dashboardsDelete.js";
import { dashboardsDuplicate } from "../funcs/dashboardsDuplicate.js";
import { dashboardsDuplicateBuiltIn } from "../funcs/dashboardsDuplicateBuiltIn.js";
import { dashboardsGet } from "../funcs/dashboardsGet.js";
import { dashboardsList } from "../funcs/dashboardsList.js";
import { dashboardsRemoveWidget } from "../funcs/dashboardsRemoveWidget.js";
import { dashboardsSaveFilters } from "../funcs/dashboardsSaveFilters.js";
import { dashboardsSaveLayout } from "../funcs/dashboardsSaveLayout.js";
import { dashboardsUpdate } from "../funcs/dashboardsUpdate.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { Dashboard } from "../models/components/dashboard.js";
import { ListDashboardsResult } from "../models/components/listdashboardsresult.js";
import {
  AddDashboardWidgetRequest,
  AddDashboardWidgetSecurity,
} from "../models/operations/adddashboardwidget.js";
import {
  CreateDashboardRequest,
  CreateDashboardSecurity,
} from "../models/operations/createdashboard.js";
import {
  DeleteDashboardRequest,
  DeleteDashboardSecurity,
} from "../models/operations/deletedashboard.js";
import {
  DuplicateBuiltInDashboardRequest,
  DuplicateBuiltInDashboardSecurity,
} from "../models/operations/duplicatebuiltindashboard.js";
import {
  DuplicateDashboardRequest,
  DuplicateDashboardSecurity,
} from "../models/operations/duplicatedashboard.js";
import {
  GetDashboardRequest,
  GetDashboardSecurity,
} from "../models/operations/getdashboard.js";
import {
  ListDashboardsRequest,
  ListDashboardsSecurity,
} from "../models/operations/listdashboards.js";
import {
  RemoveDashboardWidgetRequest,
  RemoveDashboardWidgetSecurity,
} from "../models/operations/removedashboardwidget.js";
import {
  SaveDashboardFiltersRequest,
  SaveDashboardFiltersSecurity,
} from "../models/operations/savedashboardfilters.js";
import {
  SaveDashboardLayoutRequest,
  SaveDashboardLayoutSecurity,
} from "../models/operations/savedashboardlayout.js";
import {
  UpdateDashboardRequest,
  UpdateDashboardSecurity,
} from "../models/operations/updatedashboard.js";
import { unwrapAsync } from "../types/fp.js";

export class Dashboards extends ClientSDK {
  /**
   * addDashboardWidget dashboards
   *
   * @remarks
   * Place a saved widget on a dashboard, as a new card at the bottom, sized for its chart type.
   */
  async addWidget(
    request: AddDashboardWidgetRequest,
    security?: AddDashboardWidgetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Dashboard> {
    return unwrapAsync(dashboardsAddWidget(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createDashboard dashboards
   *
   * @remarks
   * Make an empty dashboard. Any member of the project can.
   */
  async create(
    request: CreateDashboardRequest,
    security?: CreateDashboardSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Dashboard> {
    return unwrapAsync(dashboardsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteDashboard dashboards
   *
   * @remarks
   * Delete a dashboard and its cards. Its widgets stay saved. Its creator can; deleting someone else's needs project write access.
   */
  async delete(
    request: DeleteDashboardRequest,
    security?: DeleteDashboardSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(dashboardsDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * duplicateDashboard dashboards
   *
   * @remarks
   * Copy a dashboard into a new one the caller owns, named "<name> (copy)". Every card's widget is copied into a new saved widget too, named the same way, so the copy is fully independent of the original. Like every widget save, each copy is validated, so a dashboard with a broken widget cannot be duplicated until the widget is fixed.
   */
  async duplicate(
    request: DuplicateDashboardRequest,
    security?: DuplicateDashboardSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Dashboard> {
    return unwrapAsync(dashboardsDuplicate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * duplicateBuiltInDashboard dashboards
   *
   * @remarks
   * Copy a Speakeasy-built dashboard into a new one the caller owns, named "<name> (copy)", with a new saved widget per card, named the same way. The copy is a project dashboard like any other and can be changed; the built-in stays as it is.
   */
  async duplicateBuiltIn(
    request: DuplicateBuiltInDashboardRequest,
    security?: DuplicateBuiltInDashboardSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Dashboard> {
    return unwrapAsync(dashboardsDuplicateBuiltIn(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getDashboard dashboards
   *
   * @remarks
   * Get one dashboard by id, with its cards and saved filters.
   */
  async get(
    request: GetDashboardRequest,
    security?: GetDashboardSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Dashboard> {
    return unwrapAsync(dashboardsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listDashboards dashboards
   *
   * @remarks
   * List the project's dashboards, most recently updated first, each with its cards.
   */
  async list(
    request?: ListDashboardsRequest | undefined,
    security?: ListDashboardsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListDashboardsResult> {
    return unwrapAsync(dashboardsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * removeDashboardWidget dashboards
   *
   * @remarks
   * Take a card off a dashboard. The widget itself stays saved.
   */
  async removeWidget(
    request: RemoveDashboardWidgetRequest,
    security?: RemoveDashboardWidgetSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Dashboard> {
    return unwrapAsync(dashboardsRemoveWidget(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * saveDashboardFilters dashboards
   *
   * @remarks
   * Store the date range and filter values a dashboard opens on, for everyone. Until saved, changes in the filter bar are the viewer's own.
   */
  async saveFilters(
    request: SaveDashboardFiltersRequest,
    security?: SaveDashboardFiltersSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Dashboard> {
    return unwrapAsync(dashboardsSaveFilters(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * saveDashboardLayout dashboards
   *
   * @remarks
   * Move and resize a dashboard's cards. Each card names an existing placement and where it now sits; cards not listed stay as they are, so a layout saved from an older view cannot take off a card someone has just added. A card is added with addWidget and taken off with removeWidget. The grid is 12 columns wide, and each chart type has a minimum size. Layout autosaves, so the last save wins.
   */
  async saveLayout(
    request: SaveDashboardLayoutRequest,
    security?: SaveDashboardLayoutSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Dashboard> {
    return unwrapAsync(dashboardsSaveLayout(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateDashboard dashboards
   *
   * @remarks
   * Rename a dashboard or change its description. Its creator can; editing someone else's needs project write access.
   */
  async update(
    request: UpdateDashboardRequest,
    security?: UpdateDashboardSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Dashboard> {
    return unwrapAsync(dashboardsUpdate(
      this,
      request,
      security,
      options,
    ));
  }
}
